/**
 * Frontend observability. A13.
 *
 * Two integrations, both env-driven so they're no-ops when their
 * respective config is absent:
 *
 *   - **Sentry** (error monitoring). Gated on cookie consent. Only
 *     initializes after the user accepts the analytics category in the
 *     CookieBanner. DSGVO-compliant default.
 *
 *   - **Plausible** (analytics). Cookie-free, anonymized IPs by design,
 *     so it's exempt from cookie-banner gating per Datenschutzerklärung
 *     §7. Initialized unconditionally when domain is configured.
 *
 * Plus a `track(event, props)` helper that fans out to Plausible when
 * loaded. Drop-in calls. They're cheap when Plausible isn't set up.
 */

import * as Sentry from '@sentry/react';
import { getConsent } from '../components/CookieBanner';

let sentryInitialized = false;
let clarityInitialized = false;

/**
 * Initialize Sentry if (a) DSN configured and (b) user consented to
 * analytics cookies. Idempotent. Safe to call repeatedly.
 */
export function initSentry() {
  if (sentryInitialized) return;
  const dsn = process.env.REACT_APP_SENTRY_DSN;
  if (!dsn) return;
  const consent = getConsent();
  if (!consent.analytics) return;

  Sentry.init({
    dsn,
    environment: process.env.REACT_APP_SENTRY_ENVIRONMENT || 'production',
    release: process.env.REACT_APP_SENTRY_RELEASE,
    // Keep traces sparse so we stay under the 5k events/mo free tier
    // until volume justifies a paid plan.
    tracesSampleRate: parseFloat(process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE || '0.1'),
    // Don't ship PII by default. Sentry strips known fields, but extra
    // safety doesn't hurt.
    sendDefaultPii: false,
  });
  sentryInitialized = true;
}

/**
 * Initialize Microsoft Clarity. Heatmaps + session recordings +
 * rage-click detection. PII auto-masked by Clarity (passwords, names,
 * emails get redacted client-side before upload). Still gated on
 * analytics consent because session recordings carry enough context
 * that the strictest DSGVO reading wants explicit opt-in.
 *
 * Project ID lives in REACT_APP_CLARITY_PROJECT_ID. The classic
 * Clarity install snippet is reproduced here as JS so we can call it
 * conditionally instead of hardcoding the script tag in index.html.
 */
export function initClarity() {
  if (clarityInitialized) return;
  const projectId = process.env.REACT_APP_CLARITY_PROJECT_ID;
  if (!projectId) return;
  const consent = getConsent();
  if (!consent.analytics) return;

  // Standard Clarity installer. Same as the snippet from
  // clarity.microsoft.com, just function-wrapped so we can re-call on
  // consent change without doubling up the script tag.
  (function (c, l, a, r, i, t, y) {
    c[a] = c[a] || function () { (c[a].q = c[a].q || []).push(arguments); };
    t = l.createElement(r); t.async = 1; t.src = 'https://www.clarity.ms/tag/' + i;
    t.dataset.injected = 'clarity';
    y = l.getElementsByTagName(r)[0]; y.parentNode.insertBefore(t, y);
  })(window, document, 'clarity', 'script', projectId);

  clarityInitialized = true;
}

/**
 * Initialize Plausible. Loads the tracking script once. The script
 * itself is cookieless and anonymized; consent isn't legally required
 * for it under DSGVO Art. 6 lit. f (legitimate-interest analytics).
 */
export function initPlausible() {
  const domain = process.env.REACT_APP_PLAUSIBLE_DOMAIN;
  if (!domain) return;
  // Idempotency check. If the script tag is already in the DOM, skip.
  if (document.querySelector('script[data-domain]')) return;

  const apiHost = process.env.REACT_APP_PLAUSIBLE_HOST || 'https://plausible.real-content.at';
  const script = document.createElement('script');
  script.defer = true;
  script.setAttribute('data-domain', domain);
  // The `script.tagged-events` extension lets us track custom events
  // via `window.plausible(...)`. We use it for signup/wizard funnel.
  script.src = `${apiHost}/js/script.tagged-events.js`;
  document.head.appendChild(script);

  // Provide the queueing stub so calls from `track()` don't crash if
  // they fire before the real script loads.
  if (!window.plausible) {
    window.plausible = function () {
      (window.plausible.q = window.plausible.q || []).push(arguments);
    };
  }
}

/**
 * Fire a custom event. No-ops if Plausible isn't set up.
 *
 *   track('signup_completed')
 *   track('wizard_step_completed', { step: 3 })
 *   track('subscription_completed', { plan: 'solo' })
 *   track('preview_email_clicked', { decision: 'approved' })
 *
 * Property names + values land verbatim in Plausible. Keep them stable
 * between deploys. Renaming an event resets its history.
 */
export function track(event, props = {}) {
  try {
    if (typeof window.plausible === 'function') {
      window.plausible(event, { props });
    }
  } catch (e) {
    // Never crash the user flow over a tracking call. We DO surface a
    // dev-tools debug log so an obviously-broken Plausible config (wrong
    // domain, blocked by adblock, etc.) is at least visible to a
    // developer with the console open. Silent-swallow used to hide
    // perfectly fixable misconfig bugs for weeks.
    // eslint-disable-next-line no-console
    console.debug('plausible tracking failed:', event, e);
  }
}

/** Listen for cookie-consent changes and (re)init consent-gated tools. */
export function wireConsentListener() {
  window.addEventListener('real-content:consent-changed', () => {
    initSentry();
    initClarity();
  });
}
